<template>
  <v-container fluid class="pa-3">
    <v-row>
      <!-- Sales Trend Chart -->
      <v-col cols="12" lg="8">
        <v-card outlined>
          <v-toolbar flat dense>
            <v-icon color="primary" class="mr-1">
              mdi-chart-line
            </v-icon>
            <v-toolbar-title class="subtitle-1 font-weight-medium">
              Sales Trend
            </v-toolbar-title>
            <v-spacer />
            <v-select
              outlined dense hide-details
              :value="graphParams.interval"
              :items="['weekly', 'monthly', 'yearly']"
              style="max-width: 130px"
              class="mr-2"
              @change="val => setGraphParam('interval', val)"
            />
            <v-text-field
              type="date" outlined dense hide-details
              :value="graphParams.date"
              style="max-width: 155px"
              class="mr-2"
              @change="val => setGraphParam('date', val)"
            />
            <v-btn icon small :loading="fetchingGraphData" @click="getGraphData">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider />
          <div style="position: relative; height: 330px">
            <sales-chart
              ref="salesChartRef"
              :data="salesChartData"
              style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
            />
          </div>
        </v-card>
      </v-col>

      <!-- Summary Stats -->
      <v-col cols="12" lg="4">
        <v-card outlined>
          <v-toolbar flat dense>
            <v-icon color="primary" class="mr-1">
              mdi-finance
            </v-icon>
            <v-toolbar-title class="subtitle-1 font-weight-medium">
              Summary
            </v-toolbar-title>
            <v-spacer />
            <dashboard-config color="default" small text @saved="onConfigSaved" />
            <v-btn icon small :loading="fetchingSummary" @click="refreshSummary">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <div v-if="summaryDateStart" class="px-3 pb-1 caption grey--text">
            {{ summaryDateStart }} – {{ summaryDateEnd }}
          </div>
          <v-divider />
          <v-card-text class="pa-1">
            <v-row dense>
              <v-col v-for="stat in summaryStats" :key="stat.key" cols="6">
                <v-card outlined class="pa-2" style="min-height: 60px">
                  <div class="d-flex align-start">
                    <v-icon
                      :color="stat.dynamic ? dynamicColor(stat.key) : stat.color"
                      size="18"
                      class="mr-2 flex-shrink-0"
                      style="margin-top: 2px"
                    >
                      {{ stat.icon }}
                    </v-icon>
                    <div style="min-width: 0; flex: 1">
                      <div class="caption grey--text text-truncate">
                        {{ stat.label }}
                      </div>
                      <v-progress-linear
                        v-if="fetchingSummary"
                        indeterminate
                        :color="stat.color || 'primary'"
                        rounded
                        height="4"
                        class="mt-1"
                      />
                      <div
                        v-else
                        class="body-2 font-weight-bold text-truncate"
                        :class="stat.dynamic ? `${dynamicColor(stat.key)}--text` : `${stat.color}--text`"
                      >
                        {{ formatAccounting(summaryData[stat.key]) }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Printouts -->
    <v-row class="mt-1">
      <v-col cols="12">
        <printouts />
      </v-col>
    </v-row>

    <!-- Most Sold Items -->
    <v-row class="mt-1">
      <v-col cols="12">
        <v-card outlined>
          <v-toolbar flat dense>
            <v-icon color="primary" class="mr-1">
              mdi-podium
            </v-icon>
            <v-toolbar-title class="subtitle-1 font-weight-medium">
              Most Sold Items
            </v-toolbar-title>
            <v-spacer />
            <v-btn-toggle
              v-model="mostSoldGroupBy"
              mandatory dense
              class="mr-1"
              @change="getMostSoldItems()"
            >
              <v-btn value="item" x-small>
                <v-icon x-small left>
                  mdi-tag-outline
                </v-icon>Item
              </v-btn>
              <v-btn value="brand" x-small>
                <v-icon x-small left>
                  mdi-label-outline
                </v-icon>Brand
              </v-btn>
            </v-btn-toggle>
            <v-btn-toggle
              v-model="mostSoldMetric"
              mandatory dense
              class="mr-1"
              @change="getMostSoldItems()"
            >
              <v-btn value="quantity" x-small>
                <v-icon x-small left>
                  mdi-cube-outline
                </v-icon>Qty
              </v-btn>
              <v-btn value="amount" x-small>
                <v-icon x-small left>
                  mdi-cash
                </v-icon>Amount
              </v-btn>
            </v-btn-toggle>
          </v-toolbar>
          <v-divider />
          <v-card-text class="pa-2">
            <div class="d-flex flex-wrap align-center" style="gap: 8px">
              <v-text-field
                type="date" dense hide-details outlined label="From"
                :value="mostSoldItemsDateRange[0]"
                style="max-width: 160px; flex: none"
                @change="dateStartChange"
              />
              <v-text-field
                type="date" dense hide-details outlined label="To"
                :value="mostSoldItemsDateRange[1]"
                style="max-width: 160px; flex: none"
                @change="dateEndChange"
              />
              <v-text-field
                v-model="mostSoldItemsLimit" label="Limit" dense hide-details outlined
                type="number"
                style="max-width: 90px; flex: none"
              />
              <v-autocomplete
                v-if="mostSoldGroupBy === 'item'"
                v-model="selectedStocks"
                :items="stockOptions"
                :item-text="stock => `${stock.productName} (${stock.stockCode})`"
                item-value="id"
                :search-input.sync="stockSearchInput"
                multiple chips dense hide-selected clearable hide-details outlined label="Stocks"
                style="max-width: 260px; flex: none"
                @update:search-input="getStocksDropdown"
              />
              <v-autocomplete
                v-else
                v-model="selectedBrands"
                :items="brandOptions"
                multiple chips dense hide-selected clearable hide-details outlined label="Brands"
                style="max-width: 260px; flex: none"
              />
              <v-btn color="primary" small :loading="fetchingMostSoldItems" @click="getMostSoldItems()">
                <v-icon left small>
                  mdi-refresh
                </v-icon>Refresh
              </v-btn>
            </div>
          </v-card-text>
          <v-divider />
          <div style="position: relative; height: 380px">
            <most-sold-items-chart
              ref="mostSoldItemsChartRef"
              :data="mostSoldItemsChartData"
              :metric="mostSoldMetric"
              style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
            />
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import appAxios from '../../extra-functions/axios.extra'
import {
  firstDayOfMonth,
  lastDayOfMonth,
  formatAccounting,
  getCurrentDate,
  useWatchEffect,
} from '../../lib/utils'
import DashboardConfig from './component/DashboardConfig.vue'
import Printouts from './components/dashboard/Printouts.vue'
import searchDelayExtra from '../../extra-functions/searchDelay.extra'

const [watchEffect, clearWatchEffect] = useWatchEffect()
const stockInputDelay = searchDelayExtra(500)

const SUMMARY_STATS = [
  { key: 'sales', label: 'Cash Sales', icon: 'mdi-cash-register', color: 'success' },
  { key: 'consumerLoans', label: 'Credit Sales', icon: 'mdi-credit-card-outline', color: 'blue' },
  { key: 'purchases', label: 'Purchases', icon: 'mdi-cart-outline', color: 'orange' },
  { key: 'expenses', label: 'Expenses', icon: 'mdi-receipt-text-outline', color: 'red' },
  { key: 'collectibles', label: 'Collectibles', icon: 'mdi-clock-outline', color: 'purple' },
  { key: 'losses', label: 'Losses', icon: 'mdi-alert-circle-outline', color: 'red' },
  { key: 'grossProfit', label: 'Gross Profit', icon: 'mdi-trending-up', color: 'teal' },
  { key: 'netProfit', label: 'Net Profit', icon: 'mdi-finance', dynamic: true },
]

export default {
  name: 'DashboardV2',
  components: {
    salesChart: () => import('./component/SalesChartV2'),
    mostSoldItemsChart: () => import('./component/MostSoldItemsChart.vue'),
    DashboardConfig,
    Printouts,
  },
  data() {
    return {
      fetchingMostSoldItems: false,
      fetchingGraphData: false,
      fetchingSummary: false,
      mostSoldItemsLimit: 5,
      mostSoldGroupBy: 'item',
      mostSoldMetric: 'quantity',
      mostSoldItemsDateRange: [firstDayOfMonth(), lastDayOfMonth()],
      summaryDateStart: firstDayOfMonth(),
      summaryDateEnd: getCurrentDate(),
      summaryStats: SUMMARY_STATS,
      salesChartData: {
        labels: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
        sales: [0, 0, 0, 0, 0, 0, 0],
        purchases: [0, 0, 0, 0, 0, 0, 0],
        loans: [0, 0, 0, 0, 0, 0, 0],
        expenses: [0, 0, 0, 0, 0, 0, 0],
        losses: [0, 0, 0, 0, 0, 0, 0],
        collections: [0, 0, 0, 0, 0, 0, 0],
      },
      mostSoldItemsChartData: {
        labels: [],
        backgroundColor: [],
        data: [],
        datasetLabel: '',
      },
      graphParams: {
        interval: 'weekly',
        date: getCurrentDate(),
      },
      summaryData: {
        sales: 0,
        consumerLoans: 0,
        purchases: 0,
        losses: 0,
        expenses: 0,
        collectibles: 0,
        transportCost: 0,
        grossProfit: 0,
        netProfit: 0,
      },
      selectedStocks: [],
      stockOptions: [],
      stockSearchInput: null,
      selectedBrands: [],
      brandOptions: [],
    }
  },
  computed: {
    ...mapState({
      axios: (state) => appAxios(state.auth.token),
      storeId: (state) => state.settings.storeId,
    }),
  },
  watch: {
    salesChartData() {
      if (!this.$refs?.salesChartRef) return
      this.$nextTick(() => this.$refs.salesChartRef.update())
    },
    mostSoldItemsChartData() {
      if (!this.$refs.mostSoldItemsChartRef) return
      this.$nextTick(() => this.$refs.mostSoldItemsChartRef.update())
    },
  },
  created() {
    this.loadConfig().then(() => {
      this.getSummaryData()
    })
    this.getMostSoldItems()
    this.getBrandsDropdown()
  },
  mounted() {
    this.getGraphData()
  },
  destroyed() {
    clearWatchEffect()
  },
  methods: {
    async loadConfig() {
      try {
        const { data } = await this.axios.get('/admin-configs')
        if (data.dashboard_begin_date) {
          this.summaryDateStart = data.dashboard_begin_date
        }
      } catch (e) {
        console.error(e)
      }
    },
    async refreshSummary() {
      await this.loadConfig()
      this.getSummaryData()
    },
    onConfigSaved() {
      this.refreshSummary()
    },
    dynamicColor(key) {
      return this.summaryData[key] >= 0 ? 'success' : 'error'
    },
    getGraphData() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          this.fetchingGraphData = true
          const { data } = await this.axios.get('/v2/dashboard/graph-data', {
            params: { ...this.graphParams },
            signal: controller.signal,
          })
          this.salesChartData = {
            labels: data.labels,
            sales: data.sales,
            purchases: data.purchases,
            losses: data.losses,
            expenses: data.expenses,
            loans: data.credit_sales,
            collections: data.consumer_payments,
          }
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingGraphData = false
        }
      }, 'getGraphData')
    },
    getSummaryData() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          this.fetchingSummary = true
          const params = {
            date_start: this.summaryDateStart,
            date_end: this.summaryDateEnd,
          }
          if (this.storeId) params.store_id = this.storeId
          const { data } = await this.axios.get('/v2/sales-summary', {
            params,
            signal: controller.signal,
          })
          this.summaryData = {
            ...this.summaryData,
            sales: data.sales ?? 0,
            consumerLoans: data.consumer_loans ?? 0,
            purchases: data.purchases ?? 0,
            losses: data.losses ?? 0,
            expenses: data.expenses ?? 0,
            transportCost: data.transport_cost ?? 0,
            collectibles: data.collectibles ?? 0,
            grossProfit: data.gross_profit ?? 0,
            netProfit: data.net_profit ?? 0,
          }
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingSummary = false
        }
      }, 'getSummaryData')
    },
    getMostSoldItems() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          this.fetchingMostSoldItems = true
          const { data } = await this.axios.get('/v2/dashboard/stock-performance-data', {
            params: {
              stocks: this.mostSoldGroupBy === 'item' ? this.selectedStocks : [],
              brandNames: this.mostSoldGroupBy === 'brand' ? this.selectedBrands : [],
              dateStart: this.mostSoldItemsDateRange[0],
              dateEnd: this.mostSoldItemsDateRange[1],
              limit: this.mostSoldItemsLimit,
              groupBy: this.mostSoldGroupBy,
              metric: this.mostSoldMetric,
            },
          })
          this.mostSoldItemsChartData = {
            labels: data.labels,
            data: this.mostSoldMetric === 'amount' ? data.amounts : data.quantities,
            datasetLabel: data.dateRange,
            backgroundColor: data.colors,
          }
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingMostSoldItems = false
        }
      }, 'getMostSoldItems')
    },
    getStocksDropdown() {
      stockInputDelay(async () => {
        try {
          const { data } = await this.axios.get('/v2/stocks/dropdown', {
            params: { search: this.stockSearchInput },
          })
          this.stockOptions = data.data ?? []
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        }
      })
    },
    async getBrandsDropdown() {
      try {
        const { data } = await this.axios.get('/v2/dashboard/brand-names')
        this.brandOptions = data
      } catch (e) {
        console.error(e)
      }
    },
    setGraphParam(key, value) {
      this.graphParams = { ...this.graphParams, [key]: value }
    },
    dateStartChange(date) {
      this.mostSoldItemsDateRange = [date, this.mostSoldItemsDateRange[1]]
    },
    dateEndChange(date) {
      this.mostSoldItemsDateRange = [this.mostSoldItemsDateRange[0], date]
    },
    formatAccounting,
  },
}
</script>
